import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { monthLabel, sourceName, SyncSource } from './qbSyncUtils';

export interface ReSyncStatus {
    branch_id: number;
    source: SyncSource;
    connected: boolean;
    imported_through_month: string | null;
    last_sync_at: string | null;
    sync_through_options: string[];
    default_sync_through: string | null;
    collect_timeout_minutes?: number;
}

interface Props {
    show: boolean;
    status: ReSyncStatus | null;
    starting: boolean;
    onClose: () => void;
    onStart: (syncThrough: string) => void;
}

/**
 * "Re-sync from QuickBooks": pick the month to sync through and confirm QuickBooks
 * is reconciled through it (same confirmation as the first import). No start date:
 * RevRex finds everything changed since the last sync by itself.
 */
const ReSyncModal: React.FC<Props> = ({ show, status, starting, onClose, onStart }) => {
    const { t } = useTranslation('scenes.QBSync');
    const [through, setThrough] = useState<string>('');
    const [reconciled, setReconciled] = useState<boolean>(false);

    useEffect(() => {
        if (show) {
            setThrough(status?.default_sync_through || '');
            setReconciled(false);
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const name = sourceName(status?.source);
    const isQbd = status?.source === 'qbd';

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">{t('start.title', 'Re-sync from {{name}}', { name })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <dl className="row small mb-2">
                    <dt className="col-6 text-muted fw-normal">{t('start.importedThrough', 'Imported through')}</dt>
                    <dd className="col-6 mb-1">{monthLabel(status?.imported_through_month) || '—'}</dd>
                </dl>
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="qbsync-through" className="fw-bold">{t('start.syncThrough', 'Sync through')}</Form.Label>
                    <Form.Select id="qbsync-through" value={through} onChange={(e) => setThrough(e.target.value)}>
                        {(status?.sync_through_options || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                    </Form.Select>
                    <Form.Text>{t('start.syncThroughHint',
                        'Changes to months already imported are always checked. New transactions are imported up to this month.')}</Form.Text>
                </Form.Group>
                <Form.Check
                    id="qbsync-reconciled"
                    type="checkbox"
                    checked={reconciled}
                    onChange={(e) => setReconciled(e.target.checked)}
                    label={t('start.reconciled', 'I confirm {{name}} is fully reconciled through {{month}}.',
                        { name, month: monthLabel(through) })}
                />
                {isQbd && (
                    <div className="alert alert-info small mt-3 mb-0" role="note">
                        {t('start.qbdNote', 'QuickBooks Desktop is read on the computer where it is installed. After you start, open the RevRex QuickBooks Connector there and click "Sync changes" within {{minutes}} minutes. This page will show the changes when it is done.',
                            { minutes: status?.collect_timeout_minutes || 10 })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-cancel" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
                <button type="button" className="btn btn-primary" disabled={!through || !reconciled || starting}
                        onClick={() => onStart(through)}>
                    {isQbd ? t('start.startQbd', 'Start re-sync') : t('start.startQbo', 'Check for changes')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReSyncModal;
